// pms-core / domain — « Pointage OTA » (CX2, docs/PMS-CHANNEX-CUTOVER-BRIEF.md
// F3): an OTA extranet's list of bookings against the PMS's, matched on the
// OTA's own booking number, in three columns — in both · only on the OTA · only
// in the PMS. Run before, during and after the switch: « only on the OTA » after
// the switch is a lost booking.
//
// Pure: the extranet's CSV export is parsed here (Booking.com, Expedia, in
// English or French — the column names are recognised, not positional), and the
// comparison takes the PMS's rows as given. No clock, no database.

import type { Cents, IsoDate } from './types.js';

export type PointageOta = 'booking' | 'expedia';

export const POINTAGE_OTA_LABELS: Record<PointageOta, string> = {
  booking: 'Booking.com',
  expedia: 'Expedia',
};

export type OtaLineStatus = 'ok' | 'cancelled' | 'no_show';

/** One booking as the extranet lists it. */
export interface OtaLine {
  ref: string;
  guest: string | null;
  arrival: IsoDate | null;
  departure: IsoDate | null;
  status: OtaLineStatus;
  /** As the extranet states it; null when the file has no amount column. */
  amountCents: Cents | null;
  /** The status as written, for the desk. */
  statusText: string | null;
}

export interface OtaExport {
  lines: OtaLine[];
  /** Guessed from the columns; null when they do not say. */
  ota: PointageOta | null;
  /** Which column each field was read from, for the screen. */
  columns: Partial<Record<keyof Omit<OtaLine, 'statusText'>, string>>;
}

export type OtaExportResult = { ok: true; value: OtaExport } | { ok: false; error: string };

// ── CSV ──────────────────────────────────────────────────────────────────────

/** RFC 4180-ish: quoted fields, doubled quotes, newlines inside quotes; the
 *  delimiter is whichever of ; , or tab the header line uses most. */
export function parseCsv(text: string): string[][] {
  const src = text.replace(/^﻿/, '');
  const firstLine = src.split(/\r?\n/, 1)[0] ?? '';
  const count = (c: string) => firstLine.split(c).length - 1;
  const delim = [';', ',', '\t'].reduce((a, b) => (count(b) > count(a) ? b : a), ',');
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i]!;
    if (quoted) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i++;
        } else quoted = false;
      } else field += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === delim) {
      row.push(field);
      field = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') i++;
      row.push(field);
      field = '';
      if (row.some((f) => f.trim() !== '')) rows.push(row);
      row = [];
    } else field += ch;
  }
  row.push(field);
  if (row.some((f) => f.trim() !== '')) rows.push(row);
  return rows;
}

// ── Columns ──────────────────────────────────────────────────────────────────

const key = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9#]/g, '');

const SYNONYMS: Record<'ref' | 'guest' | 'arrival' | 'departure' | 'status' | 'amount', string[]> = {
  ref: [
    'booknumber', 'bookingnumber', 'reservationnumber', 'numerodereservation', 'numeroreservation',
    'reservationid', 'confirmation#', 'confirmationnumber', 'confirmation', 'itinerary#', 'itinerarynumber',
    'numerodeconfirmation', 'reference', 'bookingid', 'reservationno',
  ],
  guest: ['guestnames', 'guestname', 'guest', 'nomduclient', 'nomdesclients', 'client', 'voyageur', 'nomduvoyageur', 'bookedby', 'reservepar'],
  arrival: ['checkin', 'arrivee', 'arrival', 'arrivaldate', 'datedarrivee', 'checkindate'],
  departure: ['checkout', 'depart', 'departure', 'departuredate', 'datededepart', 'checkoutdate'],
  status: ['status', 'statut', 'etat', 'reservationstatus', 'bookingstatus'],
  amount: ['price', 'prix', 'total', 'montant', 'totalamount', 'bookingamount', 'montanttotal', 'totalprice', 'amount', 'revenue'],
};

function findColumn(headers: string[], names: string[]): number {
  const keys = headers.map(key);
  for (const n of names) {
    const i = keys.indexOf(n);
    if (i >= 0) return i;
  }
  return -1;
}

// ── Values ───────────────────────────────────────────────────────────────────

const MONTHS: Record<string, number> = {
  jan: 1, janv: 1, janvier: 1, january: 1, feb: 2, fev: 2, fevr: 2, fevrier: 2, february: 2,
  mar: 3, mars: 3, march: 3, apr: 4, avr: 4, avril: 4, april: 4, may: 5, mai: 5, jun: 6, juin: 6, june: 6,
  jul: 7, juil: 7, juillet: 7, july: 7, aug: 8, aou: 8, aout: 8, august: 8, sep: 9, sept: 9, septembre: 9,
  september: 9, oct: 10, octobre: 10, october: 10, nov: 11, novembre: 11, november: 11, dec: 12,
  decembre: 12, december: 12,
};

const pad = (n: number) => String(n).padStart(2, '0');
const iso = (y: number, m: number, d: number): IsoDate | null =>
  y > 1900 && m >= 1 && m <= 12 && d >= 1 && d <= 31 ? `${y}-${pad(m)}-${pad(d)}` : null;

/** 2026-11-02 · 02/11/2026 · 2.11.2026 · 2 Nov 2026 · 2 novembre 2026 — a time after it is ignored. */
export function parseOtaDate(raw: string): IsoDate | null {
  const s = raw.trim();
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(s);
  if (m) return iso(+m[1]!, +m[2]!, +m[3]!);
  m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})/.exec(s);
  if (m) return iso(+m[3]!, +m[2]!, +m[1]!);
  m = /^(\d{1,2})\s+([A-Za-zÀ-ÿ.]+)\s+(\d{4})/.exec(s);
  if (m) {
    const mon = MONTHS[key(m[2]!)];
    return mon ? iso(+m[3]!, mon, +m[1]!) : null;
  }
  m = /^([A-Za-z.]+)\s+(\d{1,2}),?\s+(\d{4})/.exec(s);
  if (m) {
    const mon = MONTHS[key(m[1]!)];
    return mon ? iso(+m[3]!, mon, +m[2]!) : null;
  }
  return null;
}

/** « 1 234,56 € » · « EUR 1,234.56 » · « 240 » → cents; null when unreadable. */
export function parseOtaAmount(raw: string): Cents | null {
  let s = raw.replace(/[^\d,.-]/g, '');
  if (s === '' || s === '-') return null;
  const lastComma = s.lastIndexOf(',');
  const lastDot = s.lastIndexOf('.');
  if (lastComma > lastDot) s = s.replace(/\./g, '').replace(',', '.');
  else s = s.replace(/,/g, '');
  const n = Number(s);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

export function parseOtaStatus(raw: string | null): OtaLineStatus {
  const s = key(raw ?? '');
  if (s.includes('noshow')) return 'no_show';
  if (s.includes('cancel') || s.includes('annul')) return 'cancelled';
  return 'ok';
}

export const normaliseOtaRef = (ref: string): string => ref.replace(/\s+/g, '').toUpperCase();

/** Parses an extranet export. Refuses, naming the columns it found, when it
 *  cannot tell which one is the booking number or the arrival. */
export function parseOtaExport(text: string): OtaExportResult {
  const rows = parseCsv(text);
  if (rows.length < 2) return { ok: false, error: 'Fichier vide, ou sans ligne de réservation.' };
  const headers = rows[0]!.map((h) => h.trim());
  const col = {
    ref: findColumn(headers, SYNONYMS.ref),
    guest: findColumn(headers, SYNONYMS.guest),
    arrival: findColumn(headers, SYNONYMS.arrival),
    departure: findColumn(headers, SYNONYMS.departure),
    status: findColumn(headers, SYNONYMS.status),
    amount: findColumn(headers, SYNONYMS.amount),
  };
  if (col.ref < 0 || col.arrival < 0) {
    return {
      ok: false,
      error:
        `Colonnes non reconnues : il faut le numéro de réservation et la date d’arrivée. ` +
        `Colonnes du fichier : ${headers.join(' · ')}`,
    };
  }
  const cell = (r: string[], i: number) => (i < 0 ? null : (r[i] ?? '').trim() || null);
  const lines: OtaLine[] = [];
  for (const r of rows.slice(1)) {
    const ref = cell(r, col.ref);
    if (ref === null) continue;
    const statusText = cell(r, col.status);
    const amount = cell(r, col.amount);
    lines.push({
      ref: normaliseOtaRef(ref),
      guest: cell(r, col.guest),
      arrival: parseOtaDate(cell(r, col.arrival) ?? ''),
      departure: parseOtaDate(cell(r, col.departure) ?? ''),
      status: parseOtaStatus(statusText),
      statusText,
      amountCents: amount === null ? null : parseOtaAmount(amount),
    });
  }
  const hk = headers.map(key);
  const ota: PointageOta | null = hk.includes('booknumber')
    ? 'booking'
    : hk.some((h) => h.includes('expedia') || h.includes('itinerary'))
      ? 'expedia'
      : null;
  const columns: OtaExport['columns'] = {};
  for (const [k, i] of Object.entries(col)) if (i >= 0) (columns as Record<string, string>)[k === 'amount' ? 'amountCents' : k] = headers[i]!;
  return { ok: true, value: { lines, ota, columns } };
}

// ── The comparison ───────────────────────────────────────────────────────────

/** The PMS side, as the screen hands it over. */
export interface PmsPointageRow {
  id: string;
  channel: string | null;
  channelRef: string | null;
  status: 'reserved' | 'confirmed' | 'checked_in' | 'checked_out' | 'cancelled';
  noShow: boolean;
  checkinDate: IsoDate;
  checkoutDate: IsoDate;
  totalCents: Cents;
  guestName: string;
}

export type PointageDiff = 'dates' | 'status';

export interface PointageBoth {
  ota: OtaLine;
  pms: PmsPointageRow;
  diffs: PointageDiff[];
}

export interface PointageResult {
  both: PointageBoth[];
  /** On the extranet, not in the PMS. `missing` = a live booking: a lost one. */
  onlyOta: { ota: OtaLine; missing: boolean }[];
  /** In the PMS with an OTA number of this OTA, arriving inside the file's dates, not in the file. */
  onlyPms: { pms: PmsPointageRow; live: boolean }[];
  /** The arrivals the file covers — the window « only in the PMS » is read over. */
  window: { from: IsoDate; to: IsoDate } | null;
}

const OTA_CHANNEL: Record<PointageOta, RegExp> = { booking: /booking/i, expedia: /expedia/i };

const pmsCancelled = (p: PmsPointageRow) => p.status === 'cancelled';

export function pointage(lines: readonly OtaLine[], pms: readonly PmsPointageRow[], ota: PointageOta): PointageResult {
  const byRef = new Map<string, PmsPointageRow>();
  for (const p of pms) {
    if (p.channelRef) byRef.set(normaliseOtaRef(p.channelRef), p);
  }
  const both: PointageBoth[] = [];
  const onlyOta: PointageResult['onlyOta'] = [];
  const matched = new Set<string>();
  for (const l of lines) {
    const p = byRef.get(l.ref);
    if (p === undefined) {
      onlyOta.push({ ota: l, missing: l.status === 'ok' });
      continue;
    }
    matched.add(p.id);
    const diffs: PointageDiff[] = [];
    if ((l.arrival && l.arrival !== p.checkinDate) || (l.departure && l.departure !== p.checkoutDate)) diffs.push('dates');
    const otaGone = l.status !== 'ok';
    const pmsGone = pmsCancelled(p) || p.noShow;
    if (otaGone !== pmsGone) diffs.push('status');
    both.push({ ota: l, pms: p, diffs });
  }
  const arrivals = lines.map((l) => l.arrival).filter((d): d is IsoDate => d !== null).sort();
  const window = arrivals.length > 0 ? { from: arrivals[0]!, to: arrivals[arrivals.length - 1]! } : null;
  const onlyPms: PointageResult['onlyPms'] =
    window === null
      ? []
      : pms
          .filter(
            (p) =>
              !matched.has(p.id) &&
              p.channelRef !== null &&
              p.channel !== null &&
              OTA_CHANNEL[ota].test(p.channel) &&
              p.checkinDate >= window.from &&
              p.checkinDate <= window.to,
          )
          .map((p) => ({ pms: p, live: !pmsCancelled(p) }));
  return { both, onlyOta, onlyPms, window };
}
